import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  FaUser,
  FaLock,
  FaEnvelope,
  FaSave,
  FaKey,
  FaShieldAlt,
  FaDatabase,
  FaCloud,
} from "react-icons/fa";
import { updateProfile, changePassword } from "../../store/slices/authSlice";

const profileSchema = yup
  .object({
    name: yup
      .string()
      .required("Name is required")
      .min(2, "Name must be at least 2 characters"),
    email: yup
      .string()
      .required("Email is required")
      .email("Please enter a valid email"),
  })
  .required();

const passwordSchema = yup
  .object({
    currentPassword: yup.string().required("Current password is required"),
    newPassword: yup
      .string()
      .required("New password is required")
      .min(6, "Password must be at least 6 characters")
      .matches(
        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
        "Password must contain at least one uppercase letter, one lowercase letter, and one number",
      ),
    confirmPassword: yup
      .string()
      .required("Please confirm your new password")
      .oneOf([yup.ref("newPassword")], "Passwords must match"),
  })
  .required();

const AdminSettings = () => {
  const dispatch = useDispatch();
  const { user, loading } = useSelector((state) => state.auth);

  const [activeTab, setActiveTab] = useState("password");
  const [profileSuccess, setProfileSuccess] = useState("");
  const [passwordSuccess, setPasswordSuccess] = useState("");

  const profileForm = useForm({
    resolver: yupResolver(profileSchema),
    defaultValues: {
      name: user?.name || "",
      email: user?.email || "",
    },
  });

  const passwordForm = useForm({
    resolver: yupResolver(passwordSchema),
  });

  const onProfileSubmit = async (data) => {
    const result = await dispatch(updateProfile(data));
    if (result.meta.requestStatus === "fulfilled") {
      setProfileSuccess("Profile updated successfully!");
      setTimeout(() => setProfileSuccess(""), 3000);
    }
  };

  const onPasswordSubmit = async (data) => {
    const result = await dispatch(
      changePassword({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
      }),
    );
    if (result.meta.requestStatus === "fulfilled") {
      setPasswordSuccess("Password changed successfully!");
      passwordForm.reset();
      setTimeout(() => setPasswordSuccess(""), 3000);
    }
  };

  const tabs = [
    { id: "password", label: "Password", icon: FaLock },
  ];

  return (
    <>
      <Helmet>
        <title>Settings - Admin Dashboard</title>
        <meta
          name="description"
          content="Admin settings for managing profile, password, and system configuration."
        />
      </Helmet>

      <div className="space-y-5">
        <div>
          <h1 className="text-xl font-bold text-gray-900">Settings</h1>
          <p className="text-gray-500 text-sm">Manage your account and system settings</p>
        </div>

        <div>
          <div className="max-w-4xl">
            <div className="bg-white rounded-lg shadow">
              {/* Tabs */}
              <div className="border-b border-gray-200">
                <nav className="flex">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex items-center gap-2 px-6 py-4 text-sm font-medium border-b-2 ${
                          activeTab === tab.id
                            ? "border-primary text-primary"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}
                      >
                        <Icon />
                        {tab.label}
                      </button>
                    );
                  })}
                </nav>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {/* Profile Tab */}
                {activeTab === "profile" && (
                  <div>
                    <h2 className="text-lg font-semibold mb-6">
                      Profile Information
                    </h2>

                    {profileSuccess && (
                      <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-700">{profileSuccess}</p>
                      </div>
                    )}

                    <form
                      onSubmit={profileForm.handleSubmit(onProfileSubmit)}
                      className="space-y-6"
                    >
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label className="form-label">Full Name</label>
                          <input
                            type="text"
                            {...profileForm.register("name")}
                            className={`form-input ${profileForm.formState.errors.name ? "border-red-500" : ""}`}
                          />
                          {profileForm.formState.errors.name && (
                            <p className="form-error">
                              {profileForm.formState.errors.name.message}
                            </p>
                          )}
                        </div>

                        <div>
                          <label className="form-label">Email Address</label>
                          <input
                            type="email"
                            {...profileForm.register("email")}
                            className={`form-input ${profileForm.formState.errors.email ? "border-red-500" : ""}`}
                          />
                          {profileForm.formState.errors.email && (
                            <p className="form-error">
                              {profileForm.formState.errors.email.message}
                            </p>
                          )}
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn btn-primary flex items-center gap-2"
                        >
                          {loading ? (
                            <>
                              <div className="loading w-4 h-4"></div>
                              Saving...
                            </>
                          ) : (
                            <>
                              <FaSave />
                              Save Changes
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Password Tab */}
                {activeTab === "password" && (
                  <div>
                    <h2 className="text-lg font-semibold mb-6">
                      Change Password
                    </h2>

                    {passwordSuccess && (
                      <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-700">{passwordSuccess}</p>
                      </div>
                    )}

                    <form
                      onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
                      className="space-y-6"
                    >
                      <div>
                        <label className="form-label">Current Password</label>
                        <input
                          type="password"
                          {...passwordForm.register("currentPassword")}
                          className={`form-input ${passwordForm.formState.errors.currentPassword ? "border-red-500" : ""}`}
                          placeholder="Enter your current password"
                        />
                        {passwordForm.formState.errors.currentPassword && (
                          <p className="form-error">
                            {
                              passwordForm.formState.errors.currentPassword
                                .message
                            }
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="form-label">New Password</label>
                        <input
                          type="password"
                          {...passwordForm.register("newPassword")}
                          className={`form-input ${passwordForm.formState.errors.newPassword ? "border-red-500" : ""}`}
                          placeholder="Enter your new password"
                        />
                        {passwordForm.formState.errors.newPassword && (
                          <p className="form-error">
                            {passwordForm.formState.errors.newPassword.message}
                          </p>
                        )}
                      </div>

                      <div>
                        <label className="form-label">
                          Confirm New Password
                        </label>
                        <input
                          type="password"
                          {...passwordForm.register("confirmPassword")}
                          className={`form-input ${passwordForm.formState.errors.confirmPassword ? "border-red-500" : ""}`}
                          placeholder="Confirm your new password"
                        />
                        {passwordForm.formState.errors.confirmPassword && (
                          <p className="form-error">
                            {
                              passwordForm.formState.errors.confirmPassword
                                .message
                            }
                          </p>
                        )}
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <div className="flex items-start gap-3">
                          <FaKey className="text-blue-600 mt-1" />
                          <div>
                            <h3 className="text-sm font-medium text-blue-800">
                              Password Requirements
                            </h3>
                            <ul className="text-sm text-blue-700 mt-1 space-y-1">
                              <li>• At least 6 characters long</li>
                              <li>• At least one uppercase letter</li>
                              <li>• At least one lowercase letter</li>
                              <li>• At least one number</li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="flex justify-end">
                        <button
                          type="submit"
                          disabled={loading}
                          className="btn btn-primary flex items-center gap-2"
                        >
                          {loading ? (
                            <>
                              <div className="loading w-4 h-4"></div>
                              Changing...
                            </>
                          ) : (
                            <>
                              <FaSave />
                              Change Password
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                )}

                {/* System Tab */}
                {activeTab === "system" && (
                  <div>
                    <h2 className="text-lg font-semibold mb-6">
                      System Information
                    </h2>

                    <div className="space-y-6">
                      {/* System Status */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <FaDatabase className="text-green-600 text-xl" />
                            <div>
                              <h3 className="font-medium text-green-800">
                                Database
                              </h3>
                              <p className="text-sm text-green-700">
                                Connected
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <FaCloud className="text-green-600 text-xl" />
                            <div>
                              <h3 className="font-medium text-green-800">
                                File Storage
                              </h3>
                              <p className="text-sm text-green-700">
                                Available
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-3">
                            <FaEnvelope className="text-green-600 text-xl" />
                            <div>
                              <h3 className="font-medium text-green-800">
                                Email Service
                              </h3>
                              <p className="text-sm text-green-700">Active</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* System Details */}
                      <div className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-semibold mb-4">
                          System Details
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Node.js Version
                            </label>
                            <p className="text-sm text-gray-900">
                              {process.version}
                            </p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Environment
                            </label>
                            <p className="text-sm text-gray-900">Production</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              Database
                            </label>
                            <p className="text-sm text-gray-900">MongoDB</p>
                          </div>
                          <div>
                            <label className="text-sm font-medium text-gray-600">
                              File Storage
                            </label>
                            <p className="text-sm text-gray-900">Cloudinary</p>
                          </div>
                        </div>
                      </div>

                      {/* Security Settings */}
                      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                        <div className="flex items-start gap-3">
                          <FaShieldAlt className="text-yellow-600 text-xl mt-1" />
                          <div>
                            <h3 className="font-medium text-yellow-800">
                              Security Recommendations
                            </h3>
                            <ul className="text-sm text-yellow-700 mt-2 space-y-1">
                              <li>• Regularly update your password</li>
                              <li>
                                • Enable two-factor authentication when
                                available
                              </li>
                              <li>• Monitor login activity</li>
                              <li>
                                • Keep your system and dependencies updated
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminSettings;
